import moment from 'moment'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { initialize, change, getFormValues } from 'redux-form'
import { CharField, FieldLabel, FieldError, fieldProps } from 'components/forms'
import convert from 'shared/units/convert'
import { HOURS, MINUTES } from 'shared/units/constants'
import styles from './_durationField.css'
import classnames from 'classnames'

const mapStateToProps = (state) => ({
  state: state,
})

const mapDispatchToActions = {
  change,
  initialize,
}

export class DurationField extends Component {

  static propTypes = {
    ...fieldProps,
    showHours: PropTypes.bool,
    showMinutes: PropTypes.bool,
    showSeconds: PropTypes.bool,
    formName: PropTypes.string,
  }

  static defaultProps = {
    showHours: true,
    showMinutes: true,
    showSeconds: true,
  }

  componentDidMount () {
    const { defaultValue } = this.props
    if (defaultValue) { this.updateWithDefaultValue(defaultValue) }
  }

  componentWillReceiveProps (nextProps) {
    if ('defaultValue' in nextProps && nextProps.defaultValue !== this.props.defaultValue) {
      this.updateWithDefaultValue(nextProps.defaultValue)
    }
  }

  updateWithDefaultValue = (defaultValue) => {
    const { showHours, formName, name: durationName } = this.props
    const duration = moment.duration(defaultValue, 'seconds')

    // if !showHours, update minutes to include hours' time
    if (!showHours) {
      const hourMinutes = convert(duration.hours()).from(HOURS).to(MINUTES)
      this.props.change(formName, `${durationName}_mm`, duration.minutes() + hourMinutes)
    } else {
      // ...otherwise, hours are hours, minutes are minutes
      this.props.change(formName, `${durationName}_hh`, duration.hours())
      this.props.change(formName, `${durationName}_mm`, duration.minutes())
    }

    this.props.change(formName, `${durationName}_ss`, duration.seconds())
  }

  handleChange = (evt) => {
    const value = evt.target.value
    const name = evt.target.name
    const { state, formName, name: durationName } = this.props
    const formValues = getFormValues(formName)(state)
    const values = { ...formValues, [name]: value }

    const hh = values[`${durationName}_hh`]
    const mm = values[`${durationName}_mm`]
    const ss = values[`${durationName}_ss`]

    // calculate duration
    const duration = moment.duration(0)
      .add(parseInt(hh, 10) || 0, 'hour')
      .add(parseInt(mm, 10) || 0, 'minute')
      .add(parseInt(ss, 10) || 0, 'second')
      .asSeconds()

    // update the actual duration field
    this.props.change(formName, durationName, duration)

    if (this.props.onChange) {
      this.props.onChange(duration)
    }
  }

  render () {
    const {
      bold,
      afterComponent,
      labelComponent,
      errorComponent,
      disabled,
      required,
      slim,
      short,
      errorMessage,
      name,
      showHours,
      showMinutes,
      showSeconds,
    } = this.props

    const className = classnames(styles.container, this.props.className, {
      [styles.disabled]: disabled,
      [styles.slim]: slim,
      [styles.short]: short,
    })

    return (
      <div className={className}>

        <FieldLabel
          bold={bold}
          labelComponent={labelComponent}
          required={required}
          disabled={disabled}
        />

        <div className={styles.fields}>

          {showHours ?
            <div className={classnames(styles.field, styles.hours)}>
              <CharField
                slim
                type='number'
                min='0'
                step='1'
                maxLength={3}
                name={`${name}_hh`}
                id={`${name}_hh`}
                disabled={disabled}
                onChange={this.handleChange}
              />

              <label htmlFor={`${name}_hh`} className={styles.abbr}>
                <FormattedMessage id='forms.duration_field.hh' defaultMessage='hh' />
              </label>
            </div>
          : null}

          {showHours && showMinutes ?
            <span className={styles.spacer}>:</span>
          : null}

          {showMinutes ?
            <div className={styles.field}>
              <CharField
                slim
                type='number'
                min='0'
                max={showHours ? '59' : null}
                size='4'
                step='1'
                maxLength={3}
                name={`${name}_mm`}
                id={`${name}_mm`}
                disabled={disabled}
                onChange={this.handleChange}
              />

              <label htmlFor={`${name}_mm`} className={styles.abbr}>
                <FormattedMessage id='forms.duration_field.mm' defaultMessage='mm' />
              </label>
            </div>
          : null}

          {showMinutes && showSeconds ?
            <span className={styles.spacer}>:</span>
          : null}

          {showSeconds ?
            <div className={styles.field}>
              <CharField
                slim
                type='number'
                min='0'
                max='59'
                size='4'
                step='any'
                maxLength={3}
                name={`${name}_ss`}
                id={`${name}_ss`}
                disabled={disabled}
                onChange={this.handleChange}
              />

              <label htmlFor={`${name}_ss`} className={styles.abbr}>
                <FormattedMessage id='forms.duration_field.ss' defaultMessage='ss' />
              </label>
            </div>
          : null}

          {afterComponent}
        </div>

        <FieldError
          errorComponent={errorComponent}
          showError={false}
          errorMessage={errorMessage}
        />

      </div>
    )
  }

}

export default connect(mapStateToProps, mapDispatchToActions)(DurationField)

